import { UserProgressResponseSchema } from "@amzn/client-workbench-api-model";
import { useEffect, useState } from "react";

import {
  hasFailedAutomatedIdv,
  hasFailedManualIdv,
} from "@/components/IdentityCheck/helper/rivHelper";
import {
  RIV_ENTITLEMENT_AUTOMATED,
  RIV_ENTITLEMENT_MANUAL,
} from "@/components/IdentityCheck/WizardPathChooser/hooks/entitlementDefinitions";
import { EntitlementCardProps } from "@/components/IdentityCheck/WizardPathChooser/IdentityVerificationSelectionCard";
import { useUserProgress } from "@/hooks/useUserProgress";

export type WizardEntitlements = {
  entitlements: EntitlementCardProps[];
  loading: boolean;
  error: boolean;
  metadata?: WizardEntitlementsMetadata;
};

export type WizardEntitlementsMetadata = {
  isAutomatedIdvEligible: boolean;
  isManualIdvEligible: boolean;
  isAnyInFlight: boolean;
  isManualIdvInFlight: boolean;
  isManualIdvFailed: boolean;
  isAutomatedIdvInFlight: boolean;
  isAutomatedIdvFailed: boolean;
  userProgressData?: UserProgressResponseSchema;
  isOnlyAutoIvvEligible: boolean;
};

/**
 * Where the enabled wizard identity verification options comes from is not set in stone yet and there is
 * discussion during development about using an API or something to get enabled entitlements depending on a number of factors,
 * so facade the default entitlements behind a hook in preparation for the future when this is figured out to prevent
 * needing to redevelop this.
 */
export const useGetWizardEntitlements = (): WizardEntitlements => {
  // TODO: This hook is commonly used with useUserProgress in the same file, consider combining the responses since uesUserProgress is REUSED here.
  const {
    data: userProgress,
    isError: isUserProgressError,
    isFetched: isUserProgressFetched,
    isLoading: isUserProgressLoading,
  } = useUserProgress();

  // Set an initial set of entitlements, then set it again after the API completes and if it returns additional entitlements.
  const [entitlements, setEntitlements] = useState<EntitlementCardProps[]>([]);

  const [metadata, setMetadata] = useState<
    WizardEntitlementsMetadata | undefined
  >(undefined);

  // Filter and set entitlements when the useUserProgress() finishes loading.
  useEffect(() => {
    // When is Fetched is true, check the data.
    if (isUserProgressFetched && userProgress) {
      const isOnlyAutoIvvEligible =
        canUseRivAutomated(userProgress) && !canUseRivManual(userProgress);

      const tempEntitlements = [];
      if (canUseRivAutomated(userProgress)) {
        tempEntitlements.push(RIV_ENTITLEMENT_AUTOMATED);
      }
      if (canUseRivManual(userProgress)) {
        tempEntitlements.push(RIV_ENTITLEMENT_MANUAL);
      }

      /**
       * Administratively disable the option if it's failed already.
       * This covers an edge case where the user is redirected directly to LegalDisclosure by setting the wizard path state to IVV, then they are
       * able to click Cancel on the disclosure and are directed back to the wizard path chooser.
       */
      setEntitlements(
        tempEntitlements.map((entitlement) => {
          switch (entitlement.cardType) {
            case "IVV":
              entitlement.isOnlyAutoIvvEligible = isOnlyAutoIvvEligible;
              entitlement.disabled = isAutomatedIdvFailed(userProgress);
              break;
            case "ManualRIV":
              entitlement.disabled = isManualIdvFailed(userProgress);
              break;
            default:
              /* istanbul ignore next: unreachable code */
              break;
          }
          return entitlement;
        })
      );

      // Stuff this into metadata to prevent having to use a local state for each of these properties and having multiple set state calls.
      setMetadata({
        isAnyInFlight: isAnyInFlight(userProgress),
        isAutomatedIdvEligible: canUseRivAutomated(userProgress),
        isManualIdvEligible: canUseRivManual(userProgress),
        isAutomatedIdvInFlight: isAutomatedIdvInFlight(userProgress),
        isManualIdvInFlight: isManualIdvInFlight(userProgress),
        isManualIdvFailed: isManualIdvFailed(userProgress),
        isAutomatedIdvFailed: isAutomatedIdvFailed(userProgress),
        isOnlyAutoIvvEligible: isOnlyAutoIvvEligible,
      });
    }
  }, [isUserProgressFetched, userProgress]);

  return {
    loading: isUserProgressLoading,
    error: isUserProgressError,
    entitlements,
    metadata,
  };
};

/**
 * Determine if the user is eligible for Automated RIV method based on the verification statuses returned by the API.
 * @param data The response from the Get User Progress API.
 *
 * To test this in a test user interactively, set the column `AutoIdvEligible` in the icims table in dynamodb to be True.
 */
const canUseRivAutomated = (data: UserProgressResponseSchema) => {
  return data.verificationStatus.some(
    (verificationStatusItems) =>
      verificationStatusItems.VerificationType ===
        "AUTOMATED_IVV_VERIFICATION" &&
      [
        "SUBMISSION_PENDING",
        "SUBMITTED_VERIFICATION_PENDING",
        "VERIFICATION_ELIGIBLE",
      ].includes(verificationStatusItems.VerificationTypeStatus) &&
      !hasFailedAutomatedIdv()
  );
};

const canUseRivManual = (data: UserProgressResponseSchema) => {
  return data.verificationStatus.some(
    (verificationStatusItems) =>
      [
        "ON_BOARDING_LIGHT_VERIFICATION",
        "ON_BOARDING_FULL_VERIFICATION",
      ].includes(verificationStatusItems.VerificationType) &&
      verificationStatusItems.VerificationTypeStatus ===
        "VERIFICATION_ELIGIBLE" &&
      !hasFailedManualIdv()
  );
};

const isAnyInFlight = (data: UserProgressResponseSchema): boolean => {
  return data.verificationStatus.some((verificationStatusItems) =>
    ["SUBMISSION_PENDING", "SUBMITTED_VERIFICATION_PENDING"].includes(
      verificationStatusItems.VerificationTypeStatus
    )
  );
};

const isAutomatedIdvInFlight = (data: UserProgressResponseSchema): boolean => {
  return data.verificationStatus.some(
    (verificationStatusItems) =>
      verificationStatusItems.VerificationType ===
        "AUTOMATED_IVV_VERIFICATION" &&
      ["SUBMISSION_PENDING", "SUBMITTED_VERIFICATION_PENDING"].includes(
        verificationStatusItems.VerificationTypeStatus
      )
  );
};

const isManualIdvInFlight = (data: UserProgressResponseSchema): boolean => {
  return data.verificationStatus.some(
    (verificationStatusItems) =>
      [
        "ON_BOARDING_LIGHT_VERIFICATION",
        "ON_BOARDING_FULL_VERIFICATION",
      ].includes(verificationStatusItems.VerificationType) &&
      ["SUBMISSION_PENDING", "SUBMITTED_VERIFICATION_PENDING"].includes(
        verificationStatusItems.VerificationTypeStatus
      )
  );
};

const isManualIdvFailed = (data: UserProgressResponseSchema): boolean => {
  return data.verificationStatus.some(
    (verificationStatusItems) =>
      ([
        "ON_BOARDING_LIGHT_VERIFICATION",
        "ON_BOARDING_FULL_VERIFICATION",
      ].includes(verificationStatusItems.VerificationType) &&
        [
          "SUBMITTED_VERIFICATION_REJECTED",
          "SUBMITTED_VERIFICATION_REJECTED_SUSPICIOUS",
          "SUBMISSION_EXPIRED",
        ].includes(verificationStatusItems.VerificationTypeStatus)) ||
      hasFailedManualIdv()
  );
};

const isAutomatedIdvFailed = (data: UserProgressResponseSchema): boolean => {
  return data.verificationStatus.some(
    (verificationStatusItems) =>
      (verificationStatusItems.VerificationType ===
        "AUTOMATED_IVV_VERIFICATION" &&
        [
          "SUBMITTED_VERIFICATION_REJECTED",
          "SUBMITTED_VERIFICATION_REJECTED_SUSPICIOUS",
          "SUBMISSION_EXPIRED",
        ].includes(verificationStatusItems.VerificationTypeStatus)) ||
      hasFailedAutomatedIdv()
  );
};
